import { CheckCircle2, Store, AlertTriangle } from "lucide-react";
import { fmtMoney } from "@/lib/billsLanguage";

const Row = ({ k, v, tid }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-[#E4E0D8] last:border-0">
    <dt className="text-[14px] text-archival-secondary">{k}</dt>
    <dd className="text-[15px] font-medium text-archival-primary text-right break-words" data-testid={tid}>{v}</dd>
  </div>
);

export const BillReceipt = ({ bill, billId, items }) => (
  <section data-testid="bill-receipt" className="rounded-md border border-[#E4E0D8] bg-white p-5 space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
    <div className="flex items-center gap-2">
      <CheckCircle2 size={28} className="text-[var(--action)]" aria-hidden />
      <h2 className="text-[22px] font-semibold text-archival-primary">Bill confirmed</h2>
    </div>
    <dl>
      <Row k="Vendor" v={bill?.vendor?.vendor_name || "—"} tid="receipt-vendor" />
      <Row k="Bill number" v={bill?.bill_number || "—"} tid="receipt-bill-number" />
      <Row k="Bill date" v={bill?.bill_date || "—"} tid="receipt-bill-date" />
      <Row k="Total" v={fmtMoney(bill?.total_amount, bill?.currency)} tid="receipt-total" />
      <Row k="Items" v={items} tid="receipt-items" />
    </dl>
    <p className="text-[11px] font-mono text-archival-muted break-all">Ref <span data-testid="receipt-bill-id">{billId}</span></p>
  </section>
);

const VENDOR_NOTE = {
  READY_EXISTING_VENDOR: null,
  READY_CREATE_NEW_VENDOR: "New vendor — it will be created when you confirm this bill",
  MISSING_NATIVE_VENDOR: "This vendor isn't set up for Bills yet. Ask your admin to add it before confirming.",
};

export const BillHeader = ({ bill, canEdit, onChangeVendor }) => {
  const v = bill?.vendor || {};
  const note = VENDOR_NOTE[v.state];
  const learned = v.source === "LEARNED_AUTO";
  return (
    <header className="space-y-2" data-testid="bill-header">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] uppercase tracking-wider font-semibold text-archival-muted">Bill review</p>
          <h1 className="text-[22px] sm:text-[26px] font-semibold text-archival-primary leading-tight break-words flex items-center gap-2" data-testid="bill-vendor">
            <Store size={20} className="text-archival-muted shrink-0" aria-hidden />{v.vendor_name || "Unknown vendor"}
          </h1>
          <p className="text-[13px] text-archival-secondary font-mono" data-testid="bill-meta">{bill?.bill_number || "No bill number"} · {bill?.bill_date || "No date"}</p>
        </div>
        <p className="text-[18px] font-mono font-semibold text-archival-primary shrink-0" data-testid="bill-total">{fmtMoney(bill?.total_amount, bill?.currency)}</p>
      </div>
      {learned && (
        <div className="flex items-center justify-between gap-3" data-testid="vendor-learned">
          <p className="text-[12px] text-archival-muted">Converted automatically · learned from your previous confirmations</p>
          {canEdit && <button type="button" onClick={onChangeVendor} data-testid="vendor-change-btn"
            className="min-h-[44px] px-1 text-[14px] font-medium text-[var(--action)] underline-offset-2 hover:underline">Change</button>}
        </div>
      )}
      {note && (
        <p data-testid="vendor-readiness" data-state={v.state}
          className={`text-[13px] rounded-md px-3 py-2 flex items-start gap-2 ${v.state === "MISSING_NATIVE_VENDOR" ? "bg-red-50 text-red-800 border border-red-200" : "bg-archival-soft text-archival-primary"}`}>
          {v.state === "MISSING_NATIVE_VENDOR" && <AlertTriangle size={15} className="shrink-0 mt-0.5" aria-hidden />}{note}
        </p>
      )}
    </header>
  );
};
